/* eslint-disable no-unused-vars */
// A-0008.03: Typed outputs editor (V2+ TypedOutput[] shape).
// Replaces the legacy `output: { name: oel }` map. Each output: name, type,
// description, oel_expression.
import {
 useState
} from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 Label
} from 'shared/components/ui/label';
import {
 Badge
} from 'shared/components/ui/badge';
import LongTextField from 'builders/destudio/primitives/LongTextField';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 ChevronDown, ChevronRight, Plus, Trash2
} from 'lucide-react';
import {
 OelEditor
} from 'builders/destudio/primitives/oel';
import {
    TypedOutput, TypedDataKind
} from 'builders/builderOpcode/jsonMode/types';

const TYPE_OPTIONS: TypedDataKind[] = ['string', 'number', 'boolean', 'object', 'array'];
const IDENTIFIER_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

const blankOutput = (existing: TypedOutput[]): TypedOutput => {
    const base = 'output';
    let i = existing.length + 1;
    let name = `${base}_${i}`;
    while (existing.some(x => x.name === name)) name = `${base}_${++i}`;
    return {
        name,
        type: 'string',
        description: '',
        oel_expression: '',
    };
};

const TypedOutputsEditor = ({
    outputs,
    onChange,
    oelContext,
}: {
    outputs: TypedOutput[];
    onChange: (outputs: TypedOutput[]) => void;
    oelContext?: any;
}) => {
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [errors, setErrors] = useState<Record<number, string>>({});

    const update = (idx: number, patch: Partial<TypedOutput>) => {
        const next = outputs.map((it, i) => i === idx ? {
 ...it,
...patch
} : it);
        const errs: Record<number, string> = {};
        next.forEach((it, i) => {
            if (!it.name?.trim()) {
                errs[i] = 'Name is required.';
            } else if (!IDENTIFIER_REGEX.test(it.name)) {
                errs[i] = 'Must start with a letter; letters/numbers/underscores only.';
            } else if (next.some((other, j) => j !== i && other.name === it.name)) {
                errs[i] = 'Duplicate output name.';
            }
        });
        setErrors(errs);
        onChange(next);
    };

    const remove = (idx: number) => {
        onChange(outputs.filter((_, i) => i !== idx));
        const next = {
 ...errors
};
        delete next[idx];
        setErrors(next);
    };

    const add = () => {
        const created = blankOutput(outputs);
        const nextList = [...outputs, created];
        onChange(nextList);
        setExpanded(prev => ({
 ...prev,
[nextList.length - 1]: true
}));
    };

    const toggle = (idx: number) =>
        setExpanded(prev => ({
 ...prev,
[idx]: !prev[idx]
}));

    return (
        <div className="space-y-3">
            <Label>
                Outputs <span className="text-muted-foreground">(TypedOutput[] — name, type, OEL expression, description)</span>
            </Label>

            {outputs.length === 0 && (
                <p className="text-sm text-muted-foreground italic">No outputs defined.</p>
            )}

            <div className="space-y-2">
                {outputs.map((it, idx) => {
                    const isOpen = !!expanded[idx];
                    const err = errors[idx];
                    return (
                        <div key={idx} className="border rounded-md bg-card">
                            <div className="flex items-center gap-2 p-2">
                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggle(idx)}>
                                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                                <Badge variant="outline" className="font-mono text-xs">{it.name || '<unnamed>'}</Badge>
                                <Badge variant="secondary" className="text-xs">{it.type}</Badge>
                                <span className="flex-1 text-xs text-muted-foreground font-mono truncate">
                                    {it.oel_expression || '(no expression)'}
                                </span>
                                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(idx)}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                            {isOpen && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border-t">
                                    <div>
                                        <Label className="text-xs">Name</Label>
                                        <Input
                                            value={it.name}
                                            onChange={(e) => update(idx, {
 name: e.target.value
})}
                                            placeholder="output_name"
                                        />
                                        {err && <p className="text-xs text-destructive mt-1">{err}</p>}
                                    </div>
                                    <div>
                                        <Label className="text-xs">Type</Label>
                                        <Select value={it.type} onValueChange={(v) => update(idx, {
 type: v as TypedDataKind
})}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {TYPE_OPTIONS.map(t => (
                                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label className="text-xs">OEL Expression</Label>
                                        <OelEditor
                                            value={it.oel_expression || ''}
                                            onChange={(v) => update(idx, {
 oel_expression: v
})}
                                            ctx={oelContext}
                                            placeholder="@step.output.field"
                                            ariaLabel={`Output ${it.name} expression`}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <Label className="text-xs">Description</Label>
                                        <LongTextField
                                            label="Description"
                                            value={it.description || ''}
                                            onChange={(v) => update(idx, {
 description: v
})}
                                            placeholder="What does this output represent?"
                                            monospace={false}
                                            previewLines={2}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <Button type="button" size="sm" variant="outline" onClick={add}>
                <Plus className="h-4 w-4 mr-1" /> Add Output
            </Button>
        </div>
    );
};

export default TypedOutputsEditor;
